import { forwardRef, useEffect, useRef, useState } from "react";
import { Footer } from "../Footer/Footer";
import { Outlet } from "react-router-dom";
import Header from "@components/Header/Header";
import { useSelector } from "react-redux";
import type { RootState } from "@store/store";
import useNotifications from "@features/Notification/useNotifications";
import Sidebar from "@components/Sidebar/Sidebar";
import ModalManager from "@components/common/ModalManager/ModalManager";
import { useProfile } from "@features/Profile/services/GetProfile";
import ProfileModal from "@features/Profile/components/ProfileModal";
import { Dialog, DialogContent, DialogActions, Modal } from "@mui/material";
import Button from "@components/common/Button/Button";
import { Draw } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import "@components/Sidebar/Sidebar.css";

export const MainLayout = forwardRef<HTMLElement>(() => {
	const { t } = useTranslation();
	const isAuthenticated = useSelector((state: RootState) => state.user.isAuthenticated);
	const { fetchInitial } = useNotifications();
	const fetchedRef = useRef(false);
	const [showProfileModal, setShowProfileModal] = useState(false);

	const { data: profileData, refetch: refetchProfile } = useProfile();

	useEffect(() => {
		if (isAuthenticated && !fetchedRef.current) {
			fetchInitial();
			fetchedRef.current = true;
		}
	}, [isAuthenticated, fetchInitial]);

	const showSignatureRequired = isAuthenticated && profileData && !profileData.signature;

	const handleOpenProfile = () => {
		setShowProfileModal(true);
	};

	const handleCloseProfile = () => {
		setShowProfileModal(false);
		refetchProfile();
	};

	return (
		<div className="flex min-h-screen flex-col bg-background">
			<Header />
			<Sidebar />
			<div className="main-content main-content-expanded flex-1">
				<main className="page-content w-full min-w-0 p-6">
					<Outlet />
				</main>
			</div>
			<Footer />
			<ModalManager />

			<Dialog open={!!showSignatureRequired && !showProfileModal} maxWidth="xs" fullWidth disableEscapeKeyDown>
				<DialogContent className="!pt-6">
					<div className="flex flex-col items-center text-center">
						<div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary-soft">
							<Draw className="h-8 w-8 text-primary" />
						</div>
						<h2 className="mb-2 text-xl font-semibold text-foreground">
							{t("psdaCommon.profile.signatureRequired.title") || "Signature Required"}
						</h2>
						<p className="text-sm text-muted">
							{t("psdaCommon.profile.signatureRequired.message") ||
								"Please add your signature to continue using the portal."}
						</p>
					</div>
				</DialogContent>
				<DialogActions className="!px-6 !pb-6 !pt-0 justify-center">
					<Button onClick={handleOpenProfile} variant="primary">
						{t("psdaCommon.profile.signatureRequired.openProfile") || "Open Profile"}
					</Button>
				</DialogActions>
			</Dialog>

			<Modal open={showProfileModal} onClose={handleCloseProfile} className="flex items-center justify-center px-4">
				<div className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-lg bg-white">
					<ProfileModal HideProfile={handleCloseProfile} />
				</div>
			</Modal>
		</div>
	);
});
